import { useEffect, useRef } from "react";
import camelcaseKeys from "camelcase-keys";
import { useNavigate } from "react-router-dom";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import config from "../../config";
import { AXIOS } from "../axios";
import { useError } from "../../components/status/ErrorProvider";
import { useSuccess } from "../../components/status/SuccessProvider";
import {
  IDP,
  INIT_USER,
  ROLES,
  useAuth,
  User,
} from "../../components/auth/AuthProvider";

const baseUrl = config.url.VAULT;

type SignIn = {
  email: string;
  type: IDP;
};

type LocalClaim = {
  organizationId: string;
  email: string;
  lastName: string;
  firstName: string;
  password: string;
  passwordConfirmation: string;
};

type LocalSignin = {
  email: string;
  password: string;
};

type LocalSignup = {
  lastName: string;
  firstName: string;
  email: string;
  emailConfirmation: string;
  password: string;
  passwordConfirmation: string;
};

type ResetPassword = {
  token: string | undefined;
  password: string;
  passwordConfirmation: string;
};

type Invite = {
  idp: User["idp"];
  role: User["activeOrganization"]["role"];
  email: string;
  comment?: string;
};

type InviteAccept = {
  code: string;
  token: string;
};

type InviteDetails = {
  email: string;
  organization: { id: string; name: string; role: ROLES; description: string };
};

export const useVaultSignin = () => {
  const { setError } = useError();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    SignIn
  >({
    mutationKey: [`VAULT SIGN IN`],
    mutationFn: (props: SignIn) => AXIOS.post(`${baseUrl}/auth/signin`, props),
    onSuccess: (data) => {
      const authCodeUrl = data.data?.url;
      if (authCodeUrl) window.location.href = authCodeUrl;
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultSignout = () => {
  const qc = useQueryClient();
  const auth = useAuth();
  const navigate = useNavigate();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>
  >({
    mutationKey: [`VAULT SIGN OUT`],
    mutationFn: () => AXIOS.post(`${baseUrl}/auth/signout`),
    onSuccess: (data) => {
      // remove from local storage
      localStorage.removeItem("SEMIAUTH_ORG_ID");
      localStorage.removeItem("SEMIAUTH_ORG_NAME");
      localStorage.removeItem("SEMIAUTH_REDIRECT_URL");
      localStorage.removeItem("SEMIAUTH_AUTHENTICATED");

      // clear react query cache
      qc.clear();

      // clear local auth/user
      auth.setAuth(false);
      auth.setUser(INIT_USER);
      setSuccess("Signed out");

      navigate("/welcome");
    },
    onError: (error) => {
      const message = error.response?.data.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultSession = () => {
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  const session = useMutation({
    mutationKey: [`VAULT SESSION`],
    mutationFn: () => AXIOS.post(`${baseUrl}/auth/session`),
    onSuccess: (data) => {
      // ping /session route every N minutes (configurable)
      if (!intervalRef.current) {
        // console.log("starting /session loop");
        intervalRef.current = setInterval(() => {
          session.mutate();
        }, config.session.INTERVAL);
      }
    },
    onError: () => {
      // clear interval on error to prevent retry loops
      if (intervalRef.current) {
        // console.log("clearing /session loop");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
  });

  // clean up interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return session;
};

export const useVaultClaim = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    LocalClaim
  >({
    mutationKey: [`VAULT LOCAL CLAIM`],
    mutationFn: (props: LocalClaim) =>
      AXIOS.post(`${baseUrl}/auth/local/claim`, props),
    onSuccess: (data) => setSuccess("Account claimed"),
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultLocalSignup = () => {
  const { setError } = useError();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    LocalSignup
  >({
    mutationKey: [`VAULT LOCAL SIGNUP`],
    mutationFn: (props: LocalSignup) =>
      AXIOS.post(`${baseUrl}/auth/local/signup`, props),
    onSuccess: (data) => {
      const authCodeUrl = data.data?.url;
      if (authCodeUrl) window.location.href = authCodeUrl;
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultLocalSignin = () => {
  const navigate = useNavigate();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    LocalSignin
  >({
    mutationKey: [`VAULT LOCAL SIGNIN`],
    mutationFn: (props: LocalSignin) =>
      AXIOS.post(`${baseUrl}/auth/local/signin`, props),
    onSuccess: (data) => {
      // // set local user
      // auth.setAuth(true);
      // auth.setUser(user);

      localStorage.setItem("SEMIAUTH_AUTHENTICATED", "true");

      navigate("/connect");
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });
  return mutation;
};

export const useVaultForgotPassword = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    string
  >({
    mutationKey: [`VAULT FORGOT PASSWORD`],
    mutationFn: (email: string) =>
      AXIOS.post(`${baseUrl}/auth/forgot-password`, { email }),
    onSuccess: (data) => setSuccess(`Reset link sent to ${data.data.email}`),
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });
  return mutation;
};

export const useVaultResetPassword = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    ResetPassword
  >({
    mutationKey: [`VAULT RESET PASSWORD`],
    mutationFn: (body: ResetPassword) =>
      AXIOS.post(`${baseUrl}/auth/reset-password`, body),
    onSuccess: (data) => setSuccess("Password reset"),
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });
  return mutation;
};

export const useVaultResendPwLink = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    string
  >({
    mutationKey: [`VAULT RESEND PASSWORD LINK`],
    mutationFn: (token: string) =>
      AXIOS.post(`${baseUrl}/auth/resend-password-link`, { token }),
    onSuccess: (data) => setSuccess(`Reset link sent to ${data.data.email}`),
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });
  return mutation;
};

export const useVaultInvite = () => {
  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    Invite
  >({
    mutationKey: [`VAULT INVITE`],
    mutationFn: (props: Invite) =>
      AXIOS.post(`${baseUrl}/auth/invite/send`, props),
    onSuccess: (data) => {
      setSuccess("Invite sent");

      qc.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS"),
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultInviteResend = () => {
  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    string
  >({
    mutationKey: [`VAULT INVITE RESEND`],
    mutationFn: (id: string) =>
      AXIOS.post(`${baseUrl}/auth/invite/resend`, { id }),
    onSuccess: (data) => {
      setSuccess("Invite sent");

      qc.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS"),
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultInviteCancel = () => {
  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    string
  >({
    mutationKey: [`VAULT INVITE CANCEL`],
    mutationFn: (id: string) =>
      AXIOS.post(`${baseUrl}/auth/invite/cancel`, { id }),
    onSuccess: (data) => {
      setSuccess("Invite canceled");

      qc.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS"),
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultInviteDetails = (token: string) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    InviteDetails
  >({
    enabled: !!token,
    queryKey: [`VAULT INVITE DETAILS ${token}`],
    queryFn: () => AXIOS.post(`${baseUrl}/auth/invite/details`, { token }),
    select: (data) => camelcaseKeys(data.data.data, { deep: true }),
  });
};

export const useVaultInviteAccept = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    InviteAccept
  >({
    mutationKey: [`VAULT INVITE ACCEPT`],
    mutationFn: (props: InviteAccept) =>
      AXIOS.post(`${baseUrl}/auth/invite/accept`, props),
    onSuccess: (data) => {
      setSuccess("Invite accepted");
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useVaultMe = () => {
  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string; error: string }>,
    string
  >({
    mutationKey: [`VAULT ME`],
    mutationFn: (email: string) => AXIOS.post(`${baseUrl}/auth/me`, { email }),
    onSuccess: (data) => {
      const user = camelcaseKeys(data.data.data as User, { deep: true });
      setSuccess(`Hello, ${user?.firstName || user?.email}`);
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};
